/** Debounce while dragenter fires on every row the pointer crosses. */
export const SORT_ORDER_PERSIST_DEBOUNCE_MS = 400;

/** Gap added after the current max when appending a new row (inclusive). */
export const APPEND_SORT_GAP_MIN = 1;
export const APPEND_SORT_GAP_MAX = 10;

export type AppendSortGapFn = () => number;

function randomAppendGap(): number {
    return APPEND_SORT_GAP_MIN + Math.floor(Math.random() * (APPEND_SORT_GAP_MAX - APPEND_SORT_GAP_MIN + 1));
}

function maxExistingOrder(existingOrders: Array<number | undefined | null>): number {
    let maxOrder = -1;
    for (const order of existingOrders) {
        if (order != null && Number.isFinite(order) && order > maxOrder) {
            maxOrder = order;
        }
    }
    return maxOrder;
}

function nextGapValue(nextGap: AppendSortGapFn): number {
    const gap = nextGap();
    if (!Number.isFinite(gap) || gap < 1) {
        return APPEND_SORT_GAP_MIN;
    }
    return Math.floor(gap);
}

/**
 * Unique sort orders for one insert/save set. Keeps the first occurrence of each
 * existing value; fills missing and duplicate slots after the current max with
 * a random gap of 1–10. Uniqueness is only required within this set (bulk-insert
 * output tie-back), not globally.
 */
export function uniqueSortOrdersForInsertSet(
    currentOrders: Array<number | undefined | null>,
    nextGap: AppendSortGapFn = randomAppendGap,
): number[] {
    const used = new Set<number>();
    let maxOrder = maxExistingOrder(currentOrders);
    const result: number[] = [];
    for (const order of currentOrders) {
        if (order != null && Number.isFinite(order) && !used.has(order)) {
            used.add(order);
            result.push(order);
            continue;
        }
        do {
            maxOrder += nextGapValue(nextGap);
        } while (used.has(maxOrder));
        used.add(maxOrder);
        result.push(maxOrder);
    }
    return result;
}

/** One new append order: max(existing) + random 1–10. Empty list starts at 0–9. */
export function nextAppendSortOrder(
    existingOrders: Array<number | undefined | null>,
    nextGap: AppendSortGapFn = randomAppendGap,
): number {
    return nextAppendSortOrders(1, existingOrders, nextGap)[0];
}

/** `count` unique append orders after the current max, unique within this set. */
export function nextAppendSortOrders(
    count: number,
    existingOrders: Array<number | undefined | null>,
    nextGap: AppendSortGapFn = randomAppendGap,
): number[] {
    if (count <= 0) {
        return [];
    }
    const presentOrders = existingOrders.filter((order): order is number => order != null && Number.isFinite(order));
    const placeholders = new Array<undefined>(count).fill(undefined);
    return uniqueSortOrdersForInsertSet([...presentOrders, ...placeholders], nextGap).slice(presentOrders.length);
}

/**
 * Orders for one visual sequence. Reuses the current numbers when each value
 * appears once. Duplicate or missing numbers become 0..n-1 so two ids cannot
 * keep the same order.
 */
export function monotonicOrdersForSequence(count: number, existingOrders: Array<number | undefined | null>): number[] {
    const sortedExisting = existingOrders
        .filter((order): order is number => order != null && Number.isFinite(order))
        .sort((orderA, orderB) => orderA - orderB);
    if (sortedExisting.length === count && new Set(sortedExisting).size === count) {
        return sortedExisting;
    }
    return Array.from({ length: count }, (_, index) => index);
}

export interface SortOrderChange {
    id: number;
    sortOrder: number;
}

/**
 * When any row's order changes, `changes` lists every id in visual order.
 * A patch of only the rows whose number changed merges with an in-flight save:
 * the omitted row keeps the earlier number, and two ids land on the same order.
 * Duplicate current numbers are renumbered first, so a visual swap of two rows
 * that share an order still produces a change.
 */
export function changedSortOrders<T>(
    items: T[],
    getId: (item: T) => number | undefined | null,
    getSortOrder: (item: T) => number | undefined | null,
): { orders: number[]; changes: SortOrderChange[] } {
    const orders = monotonicOrdersForSequence(items.length, items.map(getSortOrder));
    const changes: SortOrderChange[] = [];
    let orderChanged = false;
    for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
        const itemId = getId(items[itemIndex]);
        if (itemId == null || !Number.isFinite(itemId)) {
            continue;
        }
        if (getSortOrder(items[itemIndex]) !== orders[itemIndex]) {
            orderChanged = true;
        }
        changes.push({ id: itemId, sortOrder: orders[itemIndex] });
    }
    if (!orderChanged) {
        return { orders, changes: [] };
    }
    return { orders, changes };
}

/** One sort-order save at a time. A later drag waits, then reads the latest list. */
export function createSortOrderSaveQueue(): (save: () => Promise<void>) => Promise<void> {
    let tail: Promise<void> = Promise.resolve();
    return (save) => {
        const run = tail.then(save, save);
        tail = run.then(
            (): void => undefined,
            (): void => undefined,
        );
        return run;
    };
}
